// src/pages/Dashboard/Admin/Coaching/components/CoachHoursDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTimes, FaUserTie } from "react-icons/fa";

import {
  formatDateFR,
  getEventCoachHour,
} from "../utils/coachingDashboardUtils";

export default function CoachHoursDrawer({
  isOpen,
  event,
  saving,
  onClose,
  onSave,
}) {
  const [coachHour, setCoachHour] = useState("0");

  useEffect(() => {
    if (!isOpen || !event) return;

    setCoachHour(String(getEventCoachHour(event)));
  }, [isOpen, event]);

  const eventTitle = event?.titre || "Événement";
  const eventDate = formatDateFR(event?.start, {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const canSubmit = useMemo(() => {
    const value = Number(coachHour);
    return Number.isFinite(value) && value >= 0 && !saving;
  }, [coachHour, saving]);

  const handleSubmit = (submitEvent) => {
    submitEvent.preventDefault();

    if (!canSubmit) return;

    onSave?.(event, coachHour);
  };

  if (!isOpen || !event) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Heures de coaching
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaUserTie className="text-indigo-600 dark:text-indigo-300" />
              Modifier les heures
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Saisissez le nombre d’heures de présence du coach sur cet
              événement.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Événement
              </p>

              <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
                {eventTitle}
              </h3>

              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                Date : <span className="font-bold">{eventDate}</span>
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Heures de présence
              </label>

              <input
                type="number"
                inputMode="decimal"
                step="0.25"
                min="0"
                value={coachHour}
                onChange={(inputEvent) => setCoachHour(inputEvent.target.value)}
                required
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                placeholder="Ex : 1.5"
              />

              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Valeurs possibles : 1, 1.5, 2.25… Le pas est réglé à 0.25 h.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}